import { currentLocale } from '../i18n'
import type { Analysis, Dialog, DialogSummary, FinalComment, FreeComment, Message, Metrics, Stroke } from './types'

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

/** Анонимный идентификатор устройства: аккаунтов в MVP нет, но дневник и
 *  участие в диалогах нужно отличать. Не привязан к личности. */
export function deviceId(): string {
  let id = localStorage.getItem('linia_device')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('linia_device', id)
  }
  return id
}

export class ApiError extends Error {
  status: number
  code: string
  constructor(message: string, status: number, code = '') {
    super(message)
    this.status = status
    this.code = code
  }
}

async function call<T>(method: string, path: string, body?: unknown, timeoutMs = 90_000): Promise<T> {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), timeoutMs)
  try {
    const r = await fetch(`${BASE}/api${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-linia-device': deviceId() },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: ctl.signal,
    })
    if (!r.ok) {
      let code = ''
      try { code = ((await r.json()) as { error?: string }).error ?? '' } catch { /* без тела */ }
      throw new ApiError(`${path} → ${r.status}`, r.status, code)
    }
    return (await r.json()) as T
  } finally {
    clearTimeout(timer)
  }
}
const post = <T>(p: string, b: unknown, t?: number) => call<T>('POST', p, b, t)
const get = <T>(p: string) => call<T>('GET', p)
const patch = <T>(p: string, b: unknown) => call<T>('PATCH', p, b)

export type CheckIn = {
  mood: number | null
  energy: number | null
  tags: string[]
  feelText: string
}

export type FirstMessage = {
  img: string
  ogImg: string | null
  question: string | null
  truth: string | null
  text?: string
  sessionId: number | null
}

export type NewMessage = {
  kind: 'drawing' | 'text' | 'guess'
  text?: string
  img?: string | null
  ogImg?: string | null
  question?: string | null
  truth?: string | null
  replyTo?: number | null
  sessionId?: number | null
}

export const api = {
  freeComment: (p: { img: string; metrics: Metrics | null }) =>
    post<FreeComment>('/ai/free', { ...p, locale: currentLocale() }),

  therapyRead: (p: { img: string; checkin: CheckIn; metrics: Metrics | null }) =>
    post<Analysis>('/ai/therapy', { ...p, locale: currentLocale() }),

  finalRead: (p: {
    before: string
    after: string
    checkin: CheckIn
    metricsBefore: Metrics | null
    metricsAfter: Metrics | null
    calmArcs: number
  }) => post<FinalComment>('/ai/final', { ...p, locale: currentLocale() }),

  ask: (p: {
    before: string
    after: string | null
    context: string
    history: { role: 'user' | 'assistant'; content: string }[]
    question: string
  }) => post<{ answer: string }>('/ai/ask', { ...p, locale: currentLocale() }),

  /* ---- диалоги ---- */
  createDialog: (p: { kind: 'duo' | 'group'; sharedTitle?: string | null; displayName: string; first: FirstMessage | null }) =>
    post<{ id: string; url: string }>('/dialogs', { ...p, locale: currentLocale() }),

  listDialogs: () => get<{ dialogs: DialogSummary[] }>('/dialogs'),

  getDialog: (id: string, before?: number) => get<Dialog>(`/dialogs/${id}${before ? `?before=${before}` : ''}`),

  joinDialog: (id: string, displayName: string) => post<{ ok: true }>(`/dialogs/${id}/join`, { displayName }),

  patchDialog: (id: string, p: { sharedTitle?: string | null; locked?: boolean }) =>
    patch<{ ok: true }>(`/dialogs/${id}`, p),

  patchMe: (id: string, p: { displayName?: string; personalTitle?: string | null; lastReadId?: number }) =>
    patch<{ ok: true }>(`/dialogs/${id}/me`, p),

  sendMessage: (id: string, m: NewMessage) =>
    post<{ messages: Message[] }>(`/dialogs/${id}/messages`, { ...m, locale: currentLocale() }),

  /** Сырая телеметрия — главный актив продукта, пишется с первого дня. */
  telemetry: (p: {
    mode: 'free' | 'therapy' | 'reply'
    captureMode: 'canvas' | 'photo'
    checkin: CheckIn
    metricsBefore: Metrics | null
    metricsAfter: Metrics | null
    calmArcs: number
    stateLabel: string
    strokesBefore: Stroke[]
    strokesAfter: Stroke[]
  }) =>
    post<{ ok: true; sessionId: number }>('/telemetry', { ...p, locale: currentLocale() }, 20_000).catch(
      () => null,
    ),
}
